import { create } from 'zustand';
import type { Tool } from '../drawings/model';
import type { ChartType, Timeframe } from './chartModel';

/** What the active chart shows, for the main toolbar's pressed buttons and the Charts menu's ticks. */
export interface ChartState {
  symbol: string;
  tf: Timeframe;
  type: ChartType;
  grid: boolean;
  volumes: boolean;
  periodSeparators: boolean;
  autoScroll: boolean;
  chartShift: boolean;
  oneClick: boolean;
  /** The drawing tool being placed, 'measure', or null for the plain pointer. */
  tool: Tool | 'measure' | null;
}

/**
 * The active chart's commands, for the main toolbar and the menu bar (in the classic workspace the charts have no toolbar of
 * their own). The chart that becomes active registers itself; the menus act on whatever chart that is.
 */
export interface ChartCommands {
  id: string;
  state: ChartState;
  /** Shows another symbol on this chart (it stops following Market Watch). */
  setSymbol(symbol: string): void;
  setType(type: ChartType): void;
  setTimeframe(tf: Timeframe): void;
  zoom(direction: 1 | -1): void;
  toLatest(): void;
  step(): void;
  toggle(what: 'grid' | 'volumes' | 'periodSeparators' | 'autoScroll' | 'chartShift' | 'oneClick'): void;
  setTool(tool: Tool | 'measure' | null): void;
  addIndicator(id: string): void;
  applyTemplate(name: string): void;
  saveTemplate(): void;
  openProperties(): void;
  openObjects(): void;
  openIndicatorList(): void;
  saveImage(): void;
  popOut(): void;
}

interface ChartCommandState {
  active: ChartCommands | null;
  register(commands: ChartCommands): void;
  unregister(id: string): void;
}

export const useChartCommands = create<ChartCommandState>((set, get) => ({
  active: null,
  register: (commands) => set({ active: commands }),
  unregister: (id) => {
    if (get().active?.id === id) set({ active: null });
  },
}));
